'use client';

// based on https://www.radix-ui.com/primitives/docs/components/radio-group#radio-group

import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { MdCircle } from 'react-icons/md';
import { cx, cva } from 'class-variance-authority';
import styles from './radioGroup.module.scss';
import Label from '../../Label';

const radioGroupStyles = cva(styles.radioGroup, {
  variants: {
    size: {
      default: styles.default,
      fullWidth: styles.fullWidth
    }
  },
  defaultVariants: {
    size: 'default'
  }
});

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> & {
    size?: 'default' | 'fullWidth';
  }
>(({ className, size = 'default', ...props }, ref) => {
  const radioGroupClass = cx(
    styles.radioGroup,
    radioGroupStyles({ size }),
    className
  );
  return (
    <RadioGroupPrimitive.Root
      className={radioGroupClass}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cx(styles.radioGroupItem, className)}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className={styles.radioGroupIndicator}>
        <MdCircle size={14} fill="currentColor" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };

type RadioGroupItem = {
  value: string;
  label: string;
  disabled?: boolean;
  labelBefore?: boolean;
  renderAfter?: () => React.ReactNode | null;
};

type DefaultRadioGroupProps<T extends RadioGroupItem> = React.ComponentProps<
  typeof RadioGroup
> & {
  items: T[];
};

function DefaultLabel({
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof Label>) {
  return (
    <Label size="large" className={styles.label} {...props}>
      {children}
    </Label>
  );
}

export default function DefaultRadioGroup<T extends RadioGroupItem>({
  items,
  size = 'default',
  ...props
}: DefaultRadioGroupProps<T>) {
  return (
    <RadioGroup {...props}>
      {items.map((item) => (
        <div
          key={item.value}
          className={cx(styles.defaultItem, {
            [styles.fullWidth as string]: size === 'fullWidth'
          })}
        >
          <div className={styles.defaultItemInner}>
            {item.labelBefore ? (
              <DefaultLabel htmlFor={item.value}>{item.label}</DefaultLabel>
            ) : null}
            <RadioGroupItem
              value={item.value}
              id={item.value}
              disabled={item.disabled}
            />
            {!item.labelBefore ? (
              <DefaultLabel htmlFor={item.value}>{item.label}</DefaultLabel>
            ) : null}
          </div>
          {item.renderAfter && <div>{item.renderAfter()}</div>}
        </div>
      ))}
    </RadioGroup>
  );
}
